'use client';

import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logWebHeaderHovered from '@alltrails/amplitude/events/logWebHeaderHovered';
import logWebHeaderLoginButtonTapped from '@alltrails/amplitude/events/logWebHeaderLoginButtonTapped';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getFullName from '@alltrails/core/utils/getFullName';
import Avatar from '@alltrails/denali/components/Avatar';
import Button from '@alltrails/denali/components/Button';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import QrCode from '@alltrails/denali/icons/QrCode';
import { DropdownMenuOption } from '@alltrails/denali/types';
import { useExperiments } from '@alltrails/experiments';
import InstallModal from '@alltrails/install-prompt/components/InstallModal';
import { getHeaderLinkOnClickWithAnalytics, getUserLinks, getUserPermissionLinks, HeaderLink } from './headerLinks';
import styles from './styles/styles.module.scss';
import { HeaderBackground } from './types';

type UserCTAProps = {
  background: HeaderBackground;
};

const UserCTA = ({ background }: UserCTAProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const experiments = useExperiments();

  const [isOpen, setIsOpen] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);

  if (!user) {
    return (
      <Button
        linkInfo={{ href: '/login' }}
        onClick={() => {
          logWebHeaderLoginButtonTapped();
        }}
        size="sm"
        text={<FormattedMessage defaultMessage="Log in" />}
        variant={background === 'transparent-inverted' ? 'inverse' : 'primary'}
      />
    );
  }

  const openDropdown = () => {
    logWebHeaderHovered({ web_header_position: 3, web_header_section: 'my account' });
    setIsOpen(true);
  };

  // Wrapping div around the Avatar is needed for the DropdownMenu to set a ref
  const anchor = (
    <div>
      <Avatar onClick={openDropdown} size="sm" testId="header-avatar" userId={user.id} userName={getFullName(user)} />
    </div>
  );

  const options: DropdownMenuOption[] = getUserLinks({ user, languageRegionCode, experiments });
  if (isSubscriber(user)) {
    options.splice(
      1,
      0,
      { sectionHeader: null },
      {
        onClick: () => {
          setIsOpen(false);
          setShowInstallModal(true);
        },
        testId: 'header-continue-in-app',
        title: <FormattedMessage defaultMessage="Continue in app" />,
        trailingIcon: { icon: { Component: QrCode, size: 'sm' } }
      },
      { sectionHeader: null }
    );
  }
  options.push(...getUserPermissionLinks(user, languageRegionCode));

  const optionsWithAnalytics = options.map((option: HeaderLink | DropdownMenuOption, index) => {
    if ('webHeaderDropDownLink' in option) {
      const onClick = getHeaderLinkOnClickWithAnalytics(index, option.webHeaderDropDownLink, 'my account', option.onClick);
      return { ...option, onClick };
    }
    return option;
  });

  return (
    <>
      <DropdownMenu
        anchor={anchor}
        placement="bottom-end"
        className={styles.dropdownMenu}
        containerMouseHandlers={{ onMouseEnter: openDropdown, onMouseLeave: () => setIsOpen(false) }}
        isOpen={isOpen}
        onCloseRequest={() => setIsOpen(false)}
        options={optionsWithAnalytics}
        testId="header-avatar-dropdown"
      />
      <InstallModal isOpen={showInstallModal} onCloseRequest={() => setShowInstallModal(false)} location="global nav profile dropdown" />
    </>
  );
};

export default UserCTA;
